import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PartyPopper, Users, Plus, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, money } from "@/lib/api";
import { VOLUNTEER_ROLE_LABELS } from "@/lib/roles";

export default function JoinInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { registerInvite } = useAuth();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("join"); // join | create
  const [teamId, setTeamId] = useState("");
  const [newTeam, setNewTeam] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`)
      .then(({ data }) => {
        setState({ loading: false, error: null, data });
        if (data.teams?.length) setTeamId(data.teams[0].id);
        else setMode("create");
      })
      .catch((err) => setState({ loading: false, error: formatApiError(err.response?.data?.detail), data: null }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const isVol = state.data?.invite?.kind === "volunteer";
      const payload = { token, name: name.trim(), password };
      if (!isVol) {
        if (mode === "create") payload.new_team_name = newTeam.trim();
        else payload.team_id = teamId;
      }
      const user = await registerInvite(payload);
      toast.success(`Welcome aboard, ${user.name}!`);
      navigate(isVol ? "/volunteer" : "/portal");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#f8f3e9]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#e4d8c4] border-t-[#65949c]" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#f8f3e9] p-6">
        <div className="max-w-md rounded-3xl border border-[#e4d8c4] bg-white p-10 text-center">
          <Brand className="justify-center" />
          <h1 className="mt-6 font-heading text-2xl font-bold text-[#00334a]">Invite unavailable</h1>
          <p className="mt-2 text-[#7c7a6a]">{state.error}</p>
          <Link to="/login"><Button className="mt-6 rounded-full bg-[#00334a] font-bold hover:bg-[#00334a]">Go to sign in</Button></Link>
        </div>
      </div>
    );
  }

  const { campaign, teams, invite } = state.data;

  let submitLabel = "Join Hope Floats";
  if (busy) submitLabel = "Creating…";
  else if (invite.kind === "volunteer") submitLabel = "Join the team";

  return (
    <div className="grid min-h-screen bg-[#f8f3e9] lg:grid-cols-2">
      <div className="relative hidden overflow-hidden p-12 lg:flex lg:flex-col lg:justify-between">
        <img src="/brand/IMG_9505.jpeg" alt="Instructor and laughing toddler" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#00334a]/78" />
        <div className="relative z-10"><Brand light /></div>
        <div className="relative z-10">
          <PartyPopper className="h-10 w-10 text-[#e7b576]" />
          <h2 className="mt-4 font-heading text-4xl font-semibold leading-tight text-white">
            You're invited to<br />make waves.
          </h2>
          {campaign && (
            <p className="mt-4 max-w-sm text-white/85">
              Joining <strong className="text-white">{campaign.name}</strong>
              {campaign.goal ? ` · goal ${money(campaign.goal)}` : ""}.
            </p>
          )}
        </div>
        <div className="relative z-10 text-xs text-white/70">LSSP × Hope Floats Foundation</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#3f6670] hover:text-[#c97049]">
            <ArrowLeft className="h-4 w-4" /> Home
          </Link>
          <div className="lg:hidden"><Brand /></div>
          <h1 className="mt-4 font-heading text-3xl font-extrabold text-[#00334a]">Set up your account</h1>
          <p className="mt-1 text-sm text-[#7c7a6a]">Invited as <strong>{invite.email}</strong></p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="invite-setup-form">
            <div className="space-y-1.5">
              <Label>Your name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Jane Doe" data-testid="invite-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Create a password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="invite-password-input" />
            </div>

            {invite.kind === "volunteer" ? (
              <div className="rounded-xl border border-[#e4d8c4] bg-[#f8f3e9] p-4" data-testid="invite-volunteer-role">
                <div className="text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]">Your volunteer role</div>
                <div className="mt-1 font-heading text-lg font-semibold text-[#00334a]">{VOLUNTEER_ROLE_LABELS[invite.volunteer_role] || "Volunteer"}</div>
              </div>
            ) : (
            <div className="space-y-2">
              <Label>Your team</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setMode("join")} disabled={!teams.length}
                  className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold transition-colors disabled:opacity-40 ${mode === "join" ? "border-[#65949c] bg-[#efe6d6] text-[#00334a]" : "border-[#e4d8c4] text-[#7c7a6a]"}`}
                  data-testid="invite-mode-join">
                  <Users className="h-4 w-4" /> Join a team
                </button>
                <button type="button" onClick={() => setMode("create")}
                  className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold transition-colors ${mode === "create" ? "border-[#65949c] bg-[#efe6d6] text-[#00334a]" : "border-[#e4d8c4] text-[#7c7a6a]"}`}
                  data-testid="invite-mode-create">
                  <Plus className="h-4 w-4" /> Start a team
                </button>
              </div>
              {mode === "join" ? (
                <select value={teamId} onChange={(e) => setTeamId(e.target.value)}
                  className="w-full rounded-xl border border-[#e4d8c4] bg-white px-3 py-2.5 text-sm text-[#00334a]"
                  data-testid="invite-team-select">
                  {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              ) : (
                <Input value={newTeam} onChange={(e) => setNewTeam(e.target.value)} required={mode === "create"}
                  placeholder="e.g. The Wave Makers" data-testid="invite-new-team-input" />
              )}
            </div>
            )}

            <Button type="submit" disabled={busy} className="w-full rounded-full hf-btn-coral py-6 text-base font-bold" data-testid="invite-submit-button">
              {submitLabel}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
